import { EXCLUDED_FROM_ITEM_COUNT_STATUSES } from "../../../utils/orderListTable";

function titleCase(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function firstProductImageUrl(productImage) {
  if (productImage == null) return null;
  if (Array.isArray(productImage)) {
    const first = productImage.find((x) => typeof x === "string" && x.trim());
    return first || null;
  }
  if (typeof productImage === "string" && productImage.trim()) return productImage.trim();
  return null;
}

/** Weight values from API are stored in kg (show max 2 decimals e.g. 1003.23). */
export function formatOrderWeight(w) {
  const n = toPlainWeightNumber(w);
  if (n == null || n <= 0) return "—";
  const fixed = n.toFixed(2).replace(/\.?0+$/, "");
  return `${fixed} kg`;
}

/** Convert API weight (number | string | Prisma Decimal-like) to a plain number. */
export function toPlainWeightNumber(value) {
  if (value == null || value === "" || value === "-") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object") {
    if (typeof value.toNumber === "function") {
      const n = value.toNumber();
      return Number.isFinite(n) ? n : null;
    }
    if (typeof value.toString === "function" && value.toString !== Object.prototype.toString) {
      const n = Number(value.toString());
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

export function formatWeightDisplay(w) {
  const n = toPlainWeightNumber(w);
  if (n == null) return "-";
  // Max 2 decimals: 1003.225 → 1003.23, 72.5 → 72.5, 72 → 72
  return n.toFixed(2).replace(/\.?0+$/, "");
}

const ORDER_MODE_LABELS = {
  self_drop: "Self drop",
  personal_shopper: "Personal shopper",
  online_shopping: "Online",
  pick_up: "Pickup",
};

/** Normalize API / legacy values to order_type enum key. */
function resolveOrderTypeKey(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toLowerCase().replace(/\s+/g, "_");
  if (ORDER_MODE_LABELS[normalized]) return normalized;
  const aliases = {
    selfdrop: "self_drop",
    self_drop: "self_drop",
    personalshopper: "personal_shopper",
    personal_shopper: "personal_shopper",
    onlineshopping: "online_shopping",
    online_shopping: "online_shopping",
    online: "online_shopping",
    pickup: "pick_up",
    pick_up: "pick_up",
  };
  return aliases[normalized] || normalized;
}

function formatOrderMode(orderType) {
  const key = resolveOrderTypeKey(orderType);
  if (!key) return "—";
  return ORDER_MODE_LABELS[key] || titleCase(key);
}

/** Read order_type from an order_details row (item nested or flat). */
function resolveOrderDetailMode(detail) {
  const item = detail?.item || {};
  const raw =
    item.order_type ??
    detail.order_type ??
    item.mode ??
    detail.mode ??
    item.orderType ??
    detail.orderType;
  return formatOrderMode(raw);
}

function formatItemStatus(status) {
  if (!status) return "—";
  const key = String(status).trim().toLowerCase();
  if (key === "shopping_in_progress") return "In Progress";
  if (key === "received_at_the_warehouse") return "Received at the warehouse";
  if (key === "shopping_completed") return "Shopping Completed";
  return titleCase(status);
}

const STATUS_BADGE_COLORS = {
  approved: { bg: "#00C046", color: "#fff" },
  pending: { bg: "#FF8500", color: "#fff" },
  on_hold: { bg: "#126ABC", color: "#fff" },
  shopping_in_progress: { bg: "#7B61FF", color: "#fff" },
  rejected: { bg: "#FF3850", color: "#fff" },
  review: { bg: "#126ABC", color: "#fff" },
  awaiting_approval: { bg: "#FF8500", color: "#fff" },
  shopping_completed: { bg: "#00C046", color: "#fff" },
  item_delivered: { bg: "#00C046", color: "#fff" },
  received_at_the_warehouse: { bg: "#126ABC", color: "#fff" },
};

export function getItemStatusBadgeSx(status) {
  const key = String(status || "pending").toLowerCase();
  const palette = STATUS_BADGE_COLORS[key] || { bg: "#6b7280", color: "#fff" };
  return {
    display: "inline-block",
    px: 1.25,
    py: 0.35,
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    bgcolor: palette.bg,
    color: palette.color,
  };
}

export function mapOrderDetailLine(detail) {
  const item = detail?.item || {};
  const qty = detail?.quantity ?? item?.quantity ?? 1;
  const weightRaw = item?.weight ?? detail?.weight;
  const weightNum = toPlainWeightNumber(weightRaw);

  return {
    id: detail?.id || detail?.itemId || item?.id,
    itemId: item?.id || detail?.itemId || detail?.item?.id,
    name: item?.product_name || "Product",
    image: firstProductImageUrl(item?.product_image),
    quantity: qty,
    weight: weightRaw,
    weightGrams: weightNum != null && weightNum > 0 ? weightNum : 0,
    mode: resolveOrderDetailMode(detail),
    status: item?.status || detail?.status || "pending",
    statusLabel: formatItemStatus(item?.status || detail?.status),
  };
}

export function summarizeOrderLines(lines) {
  const countedLines = lines.filter((line) => {
    const status = String(line?.status ?? "")
      .trim()
      .toLowerCase();
    if (!status) return true;
    return !EXCLUDED_FROM_ITEM_COUNT_STATUSES.has(status);
  });
  const itemCount = countedLines.length;
  const totalWeight = countedLines.reduce(
    (sum, line) => sum + (line.weightGrams || 0),
    0
  );
  return {
    itemCount,
    totalWeight,
    totalWeightLabel: totalWeight > 0 ? formatOrderWeight(totalWeight) : "—",
  };
}

/** Same item filter + weight total used on order details items table. */
export function getOrderDetailsWeightSummary(orderDetails = [], orderStatus = null) {
  const details = Array.isArray(orderDetails) ? orderDetails : [];
  const paidAndBeyond = new Set(["paid", "packing", "shipment", "delivered"]);
  const status = String(orderStatus || "").toLowerCase();
  const filtered = details.filter((d) => {
    const itemStatus = String(d?.item?.status ?? d?.status ?? "").toLowerCase();
    if (itemStatus === "rejected") return false;
    if (paidAndBeyond.has(status) && itemStatus === "pending") return false;
    return true;
  });
  return summarizeOrderLines(filtered.map(mapOrderDetailLine));
}

/** Total KG for order summary — approved items only. */
export function getApprovedOrderDetailsWeightSummary(orderDetails = []) {
  const details = Array.isArray(orderDetails) ? orderDetails : [];
  const filtered = details.filter((d) => {
    const itemStatus = String(d?.item?.status ?? d?.status ?? "").toLowerCase();
    return itemStatus === "approved";
  });
  return summarizeOrderLines(filtered.map(mapOrderDetailLine));
}
